/** Значок пункта меню «Инструменты»; картинку под ключ подбирает приложение, неизвестный — как `link`. */
export type ToolIcon = 'admin' | 'grafana' | 'trace' | 'integrations' | 'api' | 'link';

/**
 * Ссылка меню «Инструменты» в шапке Front и админки (`application.tools`), открывается в новой вкладке:
 * `{ "name": "Grafana", "url": "https://grafana/d/1", "group": "Мониторинг", "icon": "grafana", "nonProdOnly": true }`.
 */
export interface IToolLink {
    name: string;
    url: string;
    /** Подпись под названием; по умолчанию — адрес ссылки. */
    description?: string;
    /** Заголовок группы в меню; без него — «Прочее». */
    group?: string;
    icon?: ToolIcon;
    /**
     * Только вне PROD: на стенде с `environment` `PROD` (без учёта регистра) пункт не показывается, без `environment` —
     * показывается. Для служебных адресов, которые есть только на тестовых стендах (`/diag`, на части стендов
     * `/monitoring/systemstatus`): меню видят и админы на PROD.
     */
    nonProdOnly?: boolean;
}

export interface IToolGroup {
    title: string;
    items: IToolLink[];
}

const OTHER_GROUP = 'Прочее';

const isProd = (environment: string | undefined) => environment?.toUpperCase() === 'PROD';

/** Показывать ли ссылку на стенде: пустые название и адрес — нет, `nonProdOnly` — только вне PROD. */
export const isToolLinkVisible = (
    link: IToolLink | null | undefined,
    environment: string | undefined,
): link is IToolLink => !!link?.name?.trim() && !!link.url?.trim() && !(link.nonProdOnly && isProd(environment));

/**
 * Группы меню «Инструменты» из ссылок в порядке приложения (свои пункты — первыми, затем `application.tools`):
 * группы — по первому появлению, без группы — «Прочее». Невидимые на стенде ссылки отбрасываются раньше повторов
 * адреса: скрытый на PROD повтор не прячет видимую ссылку.
 */
export const collectToolGroups = (
    links: ReadonlyArray<IToolLink | null | undefined>,
    environment: string | undefined,
): IToolGroup[] => {
    const seen = new Set<string>();
    const groups: IToolGroup[] = [];
    for (const link of links) {
        if (!isToolLinkVisible(link, environment)) continue;
        const url = link.url.trim();
        if (seen.has(url)) continue;
        seen.add(url);
        const title = link.group?.trim() || OTHER_GROUP;
        let group = groups.find((candidate) => candidate.title === title);
        if (!group) {
            group = { title, items: [] };
            groups.push(group);
        }
        group.items.push({ ...link, url });
    }
    return groups;
};

/** Подпись под названием по умолчанию — адрес без протокола и хвостового слэша. */
export const describeToolUrl = (url: string): string => url.replace(/^https?:\/\//, '').replace(/\/$/, '');
